import React, { useContext, useState } from 'react';
import { UserContext } from '../../userContext';
import { Avatar, Grid, TextField } from '@material-ui/core';
import {
  BusinessCenterOutlined,
  CategoryOutlined,
  EmailOutlined,
  GroupOutlined,
  PhoneOutlined,
  Storefront,
} from '@material-ui/icons';
import { useRut } from '../../../../../utils/formatRut';
import Button from '../../../../../components/common/Button';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import UsersAPI from '../../../user-api';

const emptyValue = '-';

const clientTypeLabels = {
  b2c: 'Personas (B2C)',
  b2b: 'Empresas (B2B)',
  ong: 'Fundaciones y ONG',
  gobierno: 'Gobierno',
  ambas: 'Personas (B2C), Empresas (B2B)',
};

const salesTypeLabels = {
  productos: 'Productos',
  servicios: 'Servicios',
  ambos: 'Productos y servicios',
};

const parseRegisterData = (registerData) => {
  if (!registerData) return {};

  if (typeof registerData === 'string') {
    const normalizedRegisterData = registerData.trim().replace(/^\//, '');
    if (!normalizedRegisterData) return {};

    try {
      return JSON.parse(normalizedRegisterData);
    } catch (error) {
      return {};
    }
  }

  return typeof registerData === 'object' ? registerData : {};
};

const getFirstValue = (...values) =>
  values.find((value) => {
    if (Array.isArray(value)) return value.length > 0;
    return value !== undefined && value !== null && String(value).trim() !== '';
  });

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];

  const trimmedValue = value.trim();
  if (!trimmedValue) return [];

  if (trimmedValue.startsWith('[')) {
    try {
      const parsedValue = JSON.parse(trimmedValue);
      if (Array.isArray(parsedValue)) return parsedValue;
    } catch (error) {
      return [];
    }
  }

  return trimmedValue
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const formatBoolean = (value) => {
  if (value === true) return 'Sí';
  if (value === false) return 'No';

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();
    if (['si', 'sí', 'true', '1', 'yes'].includes(normalizedValue)) return 'Sí';
    if (['no', 'false', '0'].includes(normalizedValue)) return 'No';
  }

  return emptyValue;
};

const formatList = (value, labels = {}) => {
  const values = normalizeArray(value);
  if (!values.length) return emptyValue;

  return values
    .map((item) => labels[String(item).toLowerCase()] || item)
    .join(', ');
};

const formatDisplayValue = (value) => {
  const displayValue = getFirstValue(value);
  if (displayValue === undefined) return emptyValue;
  if (Array.isArray(displayValue)) return formatList(displayValue);
  return String(displayValue);
};

const formatLocation = (location) => {
  if (!location) return '';

  return [location.address, location.commune || location.comuna, location.region]
    .filter(Boolean)
    .join(', ');
};

export const DatosEmprendimiento = ({ setShowAlert, setMessageAlert }) => {
  const [state] = useContext(UserContext);
  const history = useHistory();
  const { format } = useRut('');
  const { user } = state;
  const company = user?.company || {};
  const companyRegisterData = parseRegisterData(company?.registerData);
  const avatar = company?.logo || null;
  const [formData, setFormData] = useState({
    enterpriseType: getFirstValue(
      company?.type,
      companyRegisterData?.companySize,
      companyRegisterData?.lastYearRevenue
    ) || '',
    enterpriseSize: getFirstValue(
      company?.workforce,
      companyRegisterData?.numberOfEmployees,
      companyRegisterData?.workforce
    ) || '',
    enterpriseCategory: getFirstValue(
      company?.field,
      companyRegisterData?.productServiceCategory,
      companyRegisterData?.field,
      companyRegisterData?.sector
    ) || '',
    enterprisePhone:
      company?.telephone || companyRegisterData?.phoneNumber || '',
    enterpriseEmail:
      company?.email || companyRegisterData?.contactEmail || '',
    enterpriseName:
      company?.name ||
      company?.businessName ||
      company?.companyName ||
      companyRegisterData?.name ||
      companyRegisterData?.businessName ||
      companyRegisterData?.companyName ||
      '',
    enterpriseRut: company?.rut ? company?.rut : '',
    enterpriseFormalized: getFirstValue(
      company?.isFormalized,
      companyRegisterData?.established,
      company?.formalizationReason || companyRegisterData?.formalizationReason
        ? false
        : ''
    ),
    enterpriseDescription: company?.aboutUs ? company?.aboutUs : '',
  });

  const locationValues = normalizeArray(
    getFirstValue(company?.locations, companyRegisterData?.locations)
  )
    .map(formatLocation)
    .filter(Boolean);
  const fallbackLocation = formatLocation({
    address: companyRegisterData?.address,
    commune: companyRegisterData?.commune || companyRegisterData?.comuna,
    region: companyRegisterData?.region,
  });
  const locationDisplay = locationValues.length
    ? locationValues.join(' | ')
    : fallbackLocation || emptyValue;
  const rutDisplay = formData.enterpriseRut
    ? format(formData.enterpriseRut)
    : emptyValue;
  const clientTypesDisplay = formatList(
    getFirstValue(
      company?.clientTypes,
      companyRegisterData?.clientTypes,
      companyRegisterData?.marketing
    ),
    clientTypeLabels
  );
  const salesTypeDisplay =
    salesTypeLabels[company?.salesType] ||
    formatDisplayValue(
      getFirstValue(
        company?.salesType,
        companyRegisterData?.salesType,
        companyRegisterData?.productServiceType
      )
    );
  const formalizedDisplay = formatBoolean(formData.enterpriseFormalized);

  const summarySections = [
    {
      title: 'Información general',
      details: [
        { label: 'Nombre empresa', value: formData.enterpriseName },
        { label: 'RUT', value: rutDisplay },
        { label: 'Formalizado', value: formalizedDisplay },
        {
          label: 'Tipo formalización',
          value: getFirstValue(
            company?.formalizationType,
            companyRegisterData?.formalizationType
          ),
        },
        {
          label: 'Año formalización',
          value: getFirstValue(
            company?.formalizationYear,
            companyRegisterData?.formalizationYear
          ),
        },
        {
          label: 'Motivo no formalización',
          value: getFirstValue(
            company?.formalizationReason,
            companyRegisterData?.formalizationReason
          ),
          wide: true,
        },
        {
          label: 'Inicio del emprendimiento',
          value: getFirstValue(
            companyRegisterData?.years,
            companyRegisterData?.yearsInBusiness
          ),
        },
        {
          label: 'Descripción',
          value: formData.enterpriseDescription,
          wide: true,
        },
      ],
    },
    {
      title: 'Operaciones y desempeño',
      details: [
        { label: 'Tamaño Pyme', value: formData.enterpriseType },
        { label: 'Cantidad empleados', value: formData.enterpriseSize },
        { label: 'Productos o servicios', value: salesTypeDisplay },
        { label: 'Rubro o sector', value: formData.enterpriseCategory },
        { label: 'Clientes atendidos', value: clientTypesDisplay, wide: true },
        { label: 'Necesidad económica', value: formatBoolean(company?.economicNeeds) },
        { label: 'Opera desde hogar', value: formatBoolean(company?.homeOffice) },
        { label: 'Rubro adicional', value: company?.extraField },
      ],
    },
    {
      title: 'Contacto y redes',
      details: [
        { label: 'Teléfono contacto', value: formData.enterprisePhone },
        { label: 'Email contacto', value: formData.enterpriseEmail },
        {
          label: 'Sitio web',
          value: getFirstValue(company?.website, companyRegisterData?.website),
          wide: true,
        },
        {
          label: 'Facebook',
          value: getFirstValue(
            companyRegisterData?.facebook,
            companyRegisterData?.facebookUrl
          ),
          wide: true,
        },
        {
          label: 'Instagram',
          value: getFirstValue(
            companyRegisterData?.instagram,
            companyRegisterData?.instagramUrl
          ),
          wide: true,
        },
        {
          label: 'LinkedIn',
          value: getFirstValue(
            companyRegisterData?.linkedin,
            companyRegisterData?.linkedinUrl
          ),
          wide: true,
        },
        {
          label: 'TikTok',
          value: getFirstValue(
            companyRegisterData?.tiktok,
            companyRegisterData?.tiktokUrl
          ),
          wide: true,
        },
      ],
    },
    {
      title: 'Ubicación',
      details: [{ label: 'Dirección registrada', value: locationDisplay, wide: true }],
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const onBack = () => {
    history.push('/usuarios');
  };

  const handleSubmitMentor = ({
    email,
    status,
    pillar = null,
    password = null,
    bio = '',
    linkedin = '',
  }) => {
    let payload = {};
    if (status === 'rejected') {
      payload = {
        email,
        status,
        password,
        bio,
        linkedin,
        pillar: null,
      };
    } else {
      payload = {
        email,
        status,
        password,
        bio,
        linkedin,
        pillar: pillar ? pillar.id : null,
      };
    }
    UsersAPI.updateUser(user.id, payload)
      .then((response) => {
        setMessageAlert({
          message: response.data.message,
          type: 'success',
        });
        setShowAlert(true);
      })
      .catch((e) => {
        setMessageAlert({ message: e.data.message, type: 'error' });
        setShowAlert(true);
      });
  };

  const handleSubmitAdmin = ({ email, status, password = null }) => {
    UsersAPI.updateUser(user.id, {
      email,
      status,
      password,
    })
      .then((response) => {
        setMessageAlert({
          message: response.data.message,
          type: 'success',
        });
        setShowAlert(true);
      })
      .catch((e) => {
        setMessageAlert({ message: e.data.message, type: 'error' });
        setShowAlert(true);
      });
  };

  const handleSubmitMentee = async (form) => {
    const payload = {
      companyTelephone: form.enterprisePhone,
      companyWorkforce: form.enterpriseSize,
      companyField: form.enterpriseCategory,
      companyType: form.enterpriseType,
    };

    await UsersAPI.updateUserNew(user.id, payload)
      .then((response) => {
        const [userResponse, _] = response;
        setMessageAlert({
          message: userResponse.data.message,
          type: 'success',
        });
        setShowAlert(true);
      })
      .catch((e) => {
        setMessageAlert({ message: e.data.message, type: 'error' });
        setShowAlert(true);
      });
  };

  const onSubmit = (values) => {
    if (user.role.type === 'mentee') {
      handleSubmitMentee(formDataMentee);
    } else if (user.role.type === 'mentor') {
      handleSubmitMentor({
        email,
        status,
        pillar,
        password,
        bio,
        linkedin,
      });
    } else {
      handleSubmitAdmin({ email, password, bio, linkedin, status });
    }
  };

  return (
    <div className="emprendimiento-container">
      <Grid container className="emprendimiento-layout" spacing={4}>
        <Grid item xs={12}>
          <section className="emprendimiento-hero">
            <div className="emprendimiento-hero__icon">
              <Storefront />
            </div>
            <div>
              <span>Datos emprendimiento</span>
              <h3>Información del emprendimiento</h3>
            </div>
          </section>
        </Grid>
        <Grid item xs={12} md={5}>
          <section className="emprendimiento-card emprendimiento-card--form">
            <div className="emprendimiento-card__header">
              <div>
                <span>Información editable</span>
                <h4>Características de la empresa</h4>
              </div>
              <Avatar
                src={avatar}
                className="emprendimiento-avatar"
                alt="Avatar"
              >
                {!avatar && <Storefront />}
              </Avatar>
            </div>
            <Grid container spacing={3} className="emprendimiento-form-grid">
              <Grid item xs={12}>
                <div className="emprendimiento-field">
                  <div className="emprendimiento-field__icon">
                    <BusinessCenterOutlined />
                  </div>
                  <TextField
                    name="enterpriseType"
                    label="Tipo de empresa"
                    value={formData.enterpriseType}
                    onChange={handleChange}
                    placeholder="Ingrese tipo de empresa"
                    variant="outlined"
                    className="textField-form textField-form--multiline"
                    multiline
                    rowsMax={4}
                    fullWidth
                  />
                </div>
              </Grid>
              <Grid item xs={12}>
                <div className="emprendimiento-field">
                  <div className="emprendimiento-field__icon">
                    <GroupOutlined />
                  </div>
                  <TextField
                    name="enterpriseSize"
                    label="Tamaño de empresa"
                    value={formData.enterpriseSize}
                    onChange={handleChange}
                    placeholder="Ingrese tamaño de empresa"
                    variant="outlined"
                    className="textField-form textField-form--multiline"
                    multiline
                    rowsMax={4}
                    fullWidth
                  />
                </div>
              </Grid>
              <Grid item xs={12}>
                <div className="emprendimiento-field">
                  <div className="emprendimiento-field__icon">
                    <CategoryOutlined />
                  </div>
                  <TextField
                    name="enterpriseCategory"
                    label="Rubro o sector"
                    value={formData.enterpriseCategory}
                    onChange={handleChange}
                    placeholder="Ingrese rubro o sector"
                    variant="outlined"
                    className="textField-form textField-form--multiline"
                    multiline
                    rowsMax={4}
                    fullWidth
                  />
                </div>
              </Grid>
              <Grid item xs={12}>
                <div className="emprendimiento-field">
                  <div className="emprendimiento-field__icon">
                    <PhoneOutlined />
                  </div>
                  <TextField
                    name="enterprisePhone"
                    label="Teléfono de contacto"
                    value={formData.enterprisePhone}
                    onChange={handleChange}
                    placeholder="Ingrese teléfono de contacto"
                    variant="outlined"
                    className="textField-form"
                    fullWidth
                  />
                </div>
              </Grid>
              <Grid item xs={12}>
                <div className="emprendimiento-field">
                  <div className="emprendimiento-field__icon">
                    <EmailOutlined />
                  </div>
                  <TextField
                    name="enterpriseEmail"
                    label="Email de contacto"
                    value={formData.enterpriseEmail}
                    onChange={handleChange}
                    placeholder="Ingrese email de contacto"
                    variant="outlined"
                    className="textField-form"
                    fullWidth
                  />
                </div>
              </Grid>
            </Grid>
          </section>
        </Grid>
        <Grid item xs={12} md={7}>
          <section className="emprendimiento-card emprendimiento-card--summary">
            <div className="emprendimiento-card__header">
              <div>
                <span>Resumen empresa</span>
                <h4>Datos registrados</h4>
              </div>
            </div>
            <div className="emprendimiento-summary-sections">
              {summarySections.map((section) => (
                <div className="emprendimiento-detail-section" key={section.title}>
                  <h5>{section.title}</h5>
                  <div className="emprendimiento-detail-grid">
                    {section.details.map((detail) => (
                      <div
                        className={`emprendimiento-info-item${
                          detail.wide ? ' emprendimiento-info-item--wide' : ''
                        }`}
                        key={`${section.title}-${detail.label}`}
                      >
                        <span className="label">{detail.label}</span>
                        <p className="data-label">
                          {formatDisplayValue(detail.value)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </Grid>
      </Grid>
      <Grid
        container
        className="emprendimiento-actions"
        justify="center"
        alignItems="center"
        spacing={3}
      >
        <Grid item xs={12} sm={4}>
          <Button secondary widthFull onClick={onBack}>
            Cancelar
          </Button>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Button widthFull onClick={onSubmit}>
            Guardar información
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};
